import { useCallback, useEffect, useRef, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import { WorkerActivity, getWorkerActivity } from '../api/workerActivity';

/** The window the panel opens with. */
const DEFAULT_HOURS = 6;

export interface WorkerActivityMonitor {
    /** Which app is open, or null when the panel is closed. */
    appKey: string | null;
    activity: WorkerActivity | null;
    loading: boolean;
    error: string | null;
    hours: number;
    open: (appKey: string) => void;
    close: () => void;
    setHours: (hours: number) => void;
    reload: () => void;
}

/**
 * Loads the telemetry of one function app, on demand.
 *
 * Deliberately not on a timer and not for all five apps at once. Each app costs three Kusto
 * queries, and asking for them continuously would spend a customer's Application Insights
 * quota on a panel nobody has opened. So the queries run when somebody wants to know, which
 * is the moment a diagnosis sends them here.
 */
export const useWorkerActivity = (
    customer?: string,
    environment?: string
): WorkerActivityMonitor => {
    const axios = useAxiosSwagger();

    const [appKey, setAppKey] = useState<string | null>(null);
    const [activity, setActivity] = useState<WorkerActivity | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [hours, setHours] = useState<number>(DEFAULT_HOURS);

    const mounted = useRef<boolean>(true);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    const load = useCallback(
        async (key: string, window: number): Promise<void> => {
            if (!axios || !customer || !environment) {
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const result = await getWorkerActivity(
                    axios,
                    customer,
                    environment,
                    key,
                    window
                );

                if (mounted.current) {
                    setActivity(result);
                }
            } catch (e) {
                if (mounted.current) {
                    setError(describeError(e));
                }
            } finally {
                if (mounted.current) {
                    setLoading(false);
                }
            }
        },
        [axios, customer, environment]
    );

    // Switching environment has to close the panel: leaving it open would show one
    // customer's telemetry under another customer's heading.
    useEffect(() => {
        setAppKey(null);
        setActivity(null);
        setError(null);
    }, [customer, environment]);

    useEffect(() => {
        if (!appKey) {
            return;
        }

        void load(appKey, hours);
    }, [appKey, hours, load]);

    const open = useCallback((key: string) => {
        setActivity(null);
        setAppKey(key);
    }, []);

    const close = useCallback(() => {
        setAppKey(null);
        setActivity(null);
        setError(null);
    }, []);

    const reload = useCallback(() => {
        if (appKey) {
            void load(appKey, hours);
        }
    }, [appKey, hours, load]);

    return {
        appKey,
        activity,
        loading,
        error,
        hours,
        open,
        close,
        setHours,
        reload,
    };
};
